import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Radio, Image as ImageIcon, Video as VideoIcon, Link as LinkIcon, FileText, Megaphone,
  Send, Pencil, Trash2, ExternalLink, X, Upload, Share2
} from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { getMyProfile, removeRepresentativeToken } from '../../services/representativeService';
import AssociativeAppShell from '../../components/AssociativeAppShell';
import ConfirmDialog from '../../components/associative/AssocConfirmDialog';
import RichEditor from '../../components/RichEditor';
import type { LivePost, LiveMediaItem } from '../../services/liveService';
import { UPLOADS_BASE, buildStreamIframeUrl } from '../../services/liveService';
import * as tus from 'tus-js-client';
import { extractYouTubeId } from '../../utils/youtube';
import '../../styles/live-event.css';

interface RepProfile {
  _id: string;
  nome: string;
  cognome: string;
  tipo: 'interno' | 'esterno';
  caricaPrincipale?: string;
  foto?: string | null;
  permissions: { name: string; description: string }[];
}

type ComposerKind = 'text' | 'photo' | 'video' | 'link' | 'article';

interface LiveItem {
  _id: string;
  title: string;
  slug: string;
  subtitle?: string;
  status: string;
  isPublic?: boolean;
}

type ReporterPost = LivePost & { isOwn?: boolean };

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

// Istanza axios dedicata al pannello reporter: stessa chiave token (representative_token)
// e header Authorization Bearer usata dal resto dell'area associativa.
const liveAxios = axios.create({ baseURL: `${API_BASE}/representative/live` });
liveAxios.interceptors.request.use(config => {
  const token = localStorage.getItem('representative_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const COMPOSER_TABS: { id: ComposerKind; label: string; icon: React.ReactNode }[] = [
  { id: 'text',    label: 'Aggiornamento', icon: <Megaphone className="w-3.5 h-3.5" /> },
  { id: 'photo',   label: 'Foto',          icon: <ImageIcon className="w-3.5 h-3.5" /> },
  { id: 'video',   label: 'Video',         icon: <VideoIcon className="w-3.5 h-3.5" /> },
  { id: 'link',    label: 'Link',          icon: <LinkIcon className="w-3.5 h-3.5" /> },
  { id: 'article', label: 'Articolo blog', icon: <FileText className="w-3.5 h-3.5" /> },
];

const PLACEHOLDERS: Record<ComposerKind, string> = {
  text:    'Aggiorna gli associati su cosa sta accadendo ora…',
  photo:   'Aggiungi una didascalia (opzionale) e seleziona una o più foto.',
  video:   'Aggiungi una didascalia (opzionale) e seleziona un video.',
  link:    "Incolla l'URL — riconosciamo YouTube, Vimeo e altri.",
  article: "Incolla l'URL dell'articolo del blog (es. https://studi.unasca.it/blog/...)",
};

const buildInitials = (s: string) =>
  s.trim().split(/\s+/).map(p => p[0] || '').slice(0, 2).join('').toUpperCase() || 'CC';

const mediaSrc = (url?: string) => {
  if (!url) return '';
  return /^https?:\/\//.test(url) ? url : `${UPLOADS_BASE}${url}`;
};

const formatTime = (dateStr: string): string => {
  try {
    return new Date(dateStr).toLocaleString('it-IT', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return dateStr;
  }
};

const LiveReporterPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [profile, setProfile] = useState<RepProfile | null>(null);
  const [lives, setLives] = useState<LiveItem[]>([]);
  const [selectedLiveId, setSelectedLiveId] = useState('');
  const [loadingLives, setLoadingLives] = useState(true);

  const [posts, setPosts] = useState<ReporterPost[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(false);

  // Composer
  const [kind, setKind] = useState<ComposerKind>('text');
  const [caption, setCaption] = useState('');
  const [text, setText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [mediaUrl, setMediaUrl] = useState(''); // singolo (video)
  const [streamUid, setStreamUid] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [mediaItems, setMediaItems] = useState<LiveMediaItem[]>([]); // multipli (foto)
  const [uploading, setUploading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Edit
  const [editTarget, setEditTarget] = useState<ReporterPost | null>(null);
  const [editCaption, setEditCaption] = useState('');
  const [editText, setEditText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState<ReporterPost | null>(null);
  const [deleting, setDeleting] = useState(false);

  const selectedLive = lives.find(l => l._id === selectedLiveId) || null;

  // Condividi/copia il link pubblico della diretta (Web Share API + fallback clipboard)
  const handleShareLive = useCallback(async () => {
    if (!selectedLive) return;
    const url = `${window.location.origin}/diretta/${selectedLive.slug}`;
    if (navigator.share) {
      try { await navigator.share({ title: selectedLive.title, url }); } catch { /* condivisione annullata */ }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link della diretta copiato');
    } catch {
      toast.error('Impossibile copiare il link');
    }
  }, [selectedLive, toast]);

  const handleAuthError = useCallback((err: any): boolean => {
    if (err?.response?.status === 401 || err?.response?.status === 403) {
      removeRepresentativeToken();
      navigate('/accesso-associati');
      return true;
    }
    return false;
  }, [navigate]);

  // Profilo (per layout)
  useEffect(() => {
    const token = localStorage.getItem('representative_token');
    if (!token) {
      navigate('/accesso-associati');
      return;
    }
    (async () => {
      try {
        const res = await getMyProfile();
        if (res.success) setProfile(res.data);
      } catch (err: any) {
        if (!handleAuthError(err)) toast.error('Errore caricamento profilo');
      }
    })();
  }, [navigate, toast, handleAuthError]);

  // Dirette in onda
  useEffect(() => {
    (async () => {
      try {
        setLoadingLives(true);
        const { data } = await liveAxios.get('/');
        const items: LiveItem[] = data.items || [];
        setLives(items);
        if (items.length > 0) setSelectedLiveId(items[0]._id);
      } catch (err: any) {
        if (!handleAuthError(err)) toast.error(err.response?.data?.error || 'Errore caricamento dirette');
      } finally {
        setLoadingLives(false);
      }
    })();
  }, [toast, handleAuthError]);

  // Post della diretta selezionata
  const fetchPosts = useCallback(async (liveId: string) => {
    try {
      setLoadingPosts(true);
      const { data } = await liveAxios.get(`/${liveId}/posts`);
      setPosts(data.posts || []);
    } catch (err: any) {
      if (!handleAuthError(err)) toast.error(err.response?.data?.error || 'Errore caricamento contenuti');
    } finally {
      setLoadingPosts(false);
    }
  }, [toast, handleAuthError]);

  useEffect(() => {
    if (selectedLiveId) fetchPosts(selectedLiveId);
    else setPosts([]);
  }, [selectedLiveId, fetchPosts]);

  const resetComposer = () => {
    setCaption('');
    setText('');
    setLinkUrl('');
    setMediaUrl('');
    setMediaItems([]);
    setStreamUid('');
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSelectKind = (k: ComposerKind) => {
    setKind(k);
    setMediaUrl('');
    setMediaItems([]);
    setLinkUrl('');
    setStreamUid('');
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      if (kind === 'photo') {
        // upload multiplo (max 10)
        const arr = Array.from(files).slice(0, 10);
        const formData = new FormData();
        arr.forEach(f => formData.append('media', f));
        const { data } = await liveAxios.post('/upload-many', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const items: LiveMediaItem[] = (data.items || []).map((i: any) => ({ url: i.url, mimeType: i.mimeType }));
        setMediaItems(prev => [...prev, ...items].slice(0, 10));
        toast.success(items.length > 1 ? `${items.length} foto caricate` : 'Foto caricata');
      } else {
        // VIDEO: prova Cloudflare Stream (upload diretto), fallback a upload locale
        const file = files[0];
        setMediaUrl(''); setStreamUid(''); setUploadProgress(0);
        let du: any = null;
        try {
          const resp = await liveAxios.post('/video/tus-create', { fileSize: file.size, fileName: file.name });
          du = resp.data;
        } catch {
          du = null; // 409 (non configurato) o errore → fallback locale
        }
        if (du && du.uploadURL && du.uid) {
          // Upload resumable (tus) direttamente a Cloudflare: a blocchi, con RESUME automatico se
          // la rete mobile cade (il vecchio POST singolo invece ripartiva da zero → falliva su 4G).
          await new Promise<void>((resolve, reject) => {
            const upload = new tus.Upload(file, {
              uploadUrl: du.uploadURL,
              chunkSize: 50 * 1024 * 1024,
              retryDelays: [0, 3000, 5000, 10000, 20000],
              onError: (err) => reject(err),
              onProgress: (sent, total) => setUploadProgress(Math.round((sent / total) * 100)),
              onSuccess: () => resolve(),
            });
            upload.start();
          });
          setStreamUid(du.uid);
          toast.success('Video caricato (in elaborazione)');
        } else {
          const formData = new FormData();
          formData.append('media', file);
          const { data } = await liveAxios.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
          if (!data.url) throw new Error('URL non disponibile');
          setMediaUrl(data.url);
          toast.success('File caricato');
        }
      }
    } catch (err: any) {
      if (!handleAuthError(err)) toast.error(err.response?.data?.error || 'Errore caricamento file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeMediaItem = (idx: number) => {
    setMediaItems(prev => prev.filter((_, i) => i !== idx));
  };

  const handlePublish = async () => {
    if (!selectedLiveId) return;

    const payload: Record<string, any> = {};
    if (caption.trim()) payload.caption = caption.trim();

    if (kind === 'text') {
      if (!text.trim() || text.replace(/<[^>]*>/g, '').trim() === '') {
        toast.error('Inserisci il testo');
        return;
      }
      payload.kind = 'text';
      payload.text = text;
    } else if (kind === 'photo') {
      if (mediaItems.length === 0) {
        toast.error('Carica almeno una foto');
        return;
      }
      payload.kind = 'photo';
      payload.mediaItems = mediaItems;
    } else if (kind === 'video') {
      if (!mediaUrl && !streamUid) {
        toast.error('Carica prima un video');
        return;
      }
      payload.kind = 'video';
      if (streamUid) payload.streamUid = streamUid; else payload.mediaUrl = mediaUrl;
    } else if (kind === 'link') {
      const url = linkUrl.trim();
      if (!url) {
        toast.error('Inserisci un URL');
        return;
      }
      // auto-rilevamento YouTube → kind=youtube
      const yt = extractYouTubeId(url);
      if (yt) {
        payload.kind = 'youtube';
        payload.youtubeId = yt;
      } else {
        payload.kind = 'link';
        payload.linkUrl = url;
      }
    } else if (kind === 'article') {
      const url = linkUrl.trim();
      if (!url) {
        toast.error("Inserisci l'URL dell'articolo");
        return;
      }
      payload.kind = 'article';
      payload.linkUrl = url;
    }

    setPublishing(true);
    try {
      const { data } = await liveAxios.post(`/${selectedLiveId}/posts`, payload);
      const newPost: ReporterPost = { ...data.item, isOwn: true };
      setPosts(prev => [newPost, ...prev]);
      resetComposer();
      toast.success('Contenuto pubblicato');
    } catch (err: any) {
      if (!handleAuthError(err)) toast.error(err.response?.data?.error || 'Errore pubblicazione');
    } finally {
      setPublishing(false);
    }
  };

  const openEdit = (post: ReporterPost) => {
    setEditTarget(post);
    setEditCaption(post.caption || '');
    setEditText(post.text || '');
  };

  const handleSaveEdit = async () => {
    if (!editTarget || !selectedLiveId) return;
    const payload: Record<string, any> = { caption: editCaption };
    if (editTarget.kind === 'text') payload.text = editText;

    setSavingEdit(true);
    try {
      const { data } = await liveAxios.put(`/${selectedLiveId}/posts/${editTarget._id}`, payload);
      setPosts(prev => prev.map(p => (p._id === editTarget._id ? { ...p, ...data.item, isOwn: true } : p)));
      toast.success('Contenuto aggiornato');
      setEditTarget(null);
    } catch (err: any) {
      if (!handleAuthError(err)) toast.error(err.response?.data?.error || 'Errore aggiornamento');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || !selectedLiveId) return;
    setDeleting(true);
    try {
      await liveAxios.delete(`/${selectedLiveId}/posts/${deleteTarget._id}`);
      setPosts(prev => prev.filter(p => p._id !== deleteTarget._id));
      toast.success('Contenuto eliminato');
    } catch (err: any) {
      if (!handleAuthError(err)) toast.error(err.response?.data?.error || 'Errore eliminazione');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  return (
    <AssociativeAppShell
      route="diretta"
      canLive
      nome={profile?.nome}
      cognome={profile?.cognome}
      caricaPrincipale={profile?.caricaPrincipale}
      tipo={profile?.tipo}
      foto={profile?.foto || undefined}
    >
      <div className="page fade-in">
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '22px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '42px', height: '42px', borderRadius: 'var(--r-md)', background: 'rgba(217,75,59,.12)', color: 'var(--un-danger)', display: 'grid', placeItems: 'center', flex: '0 0 auto' }}>
              <Radio size={21} />
            </div>
            <div>
              <div className="mono-label" style={{ color: 'var(--un-danger)' }}>Area Rappresentativa</div>
              <h1 style={{ margin: '4px 0 0', font: '700 26px var(--font-display)', letterSpacing: 'var(--tracking-tight)' }}>
                Reporter Diretta
              </h1>
            </div>
          </div>
          {selectedLive && (
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              {selectedLive.isPublic ? (
                <a
                  href={`/diretta/${selectedLive.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn ghost sm"
                >
                  <ExternalLink size={14} />
                  Apri la pagina pubblica
                </a>
              ) : (
                <span className="muted" style={{ fontSize: '12px' }}>Diretta riservata agli associati</span>
              )}
              <button
                type="button"
                onClick={handleShareLive}
                title="Condividi o copia il link della diretta"
                className="top-link"
              >
                <Share2 size={14} />
                Condividi
              </button>
            </div>
          )}
        </div>

        {loadingLives ? (
          <section className="card">
            <div className="card-body" style={{ textAlign: 'center', padding: '48px' }}>
              <div className="muted" style={{ fontSize: '13px' }}>Caricamento…</div>
            </div>
          </section>
        ) : lives.length === 0 ? (
          <section className="card">
            <div className="card-body" style={{ textAlign: 'center', padding: '48px' }}>
              <Radio size={28} className="muted" style={{ display: 'block', margin: '0 auto 10px' }} />
              <div className="muted" style={{ fontSize: '13px' }}>Nessuna diretta in onda</div>
            </div>
          </section>
        ) : (
          <>
            {/* Selettore diretta */}
            {lives.length > 1 && (
              <div className="toolbar" style={{ marginBottom: '16px' }}>
                <select
                  className="select-pill"
                  value={selectedLiveId}
                  onChange={(e) => setSelectedLiveId(e.target.value)}
                >
                  {lives.map(l => (
                    <option key={l._id} value={l._id}>{l.title}</option>
                  ))}
                </select>
              </div>
            )}

          {/* Composer (look del mockup) */}
          <div className="live-app" style={{ background: 'transparent', minHeight: 0, marginBottom: 20 }}>
            <section className="composer">
              <div className="composer-top">
                {profile?.foto ? (
                  <div className="av" style={{ background: '#000', overflow: 'hidden' }}>
                    <img src={mediaSrc(profile.foto) || profile.foto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                  </div>
                ) : (
                  <div className="av">{buildInitials(`${profile?.nome || ''} ${profile?.cognome || ''}`)}</div>
                )}
                <div className="who">
                  Stai pubblicando come {profile?.nome} {profile?.cognome}
                  <small>{profile?.caricaPrincipale || 'Content creator'} · visibile a tutti gli associati in diretta</small>
                </div>
                {selectedLive?.status === 'live' && (
                  <span className="live-tag"><span className="dot" /> In diretta</span>
                )}
              </div>

              <div className="composer-types">
                {COMPOSER_TABS.map(t => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => handleSelectKind(t.id)}
                    className={kind === t.id ? 'active' : ''}
                  >
                    {t.icon}
                    {t.label}
                  </button>
                ))}
              </div>

              {kind === 'text' && (
                <div style={{ marginBottom: 12 }}>
                  <RichEditor
                    value={text}
                    onChange={setText}
                    height={200}
                    placeholder={PLACEHOLDERS.text}
                    compact
                  />
                </div>
              )}

              {kind === 'photo' && (
                <div style={{ marginBottom: 12 }}>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading || mediaItems.length >= 10}
                    style={{
                      width: '100%', padding: '12px 14px',
                      borderRadius: 12, border: '2px dashed var(--un-ink-300)',
                      background: 'var(--un-ink-50)', color: 'var(--un-ink-500)',
                      fontSize: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      cursor: 'pointer'
                    }}
                  >
                    <Upload width={16} height={16} />
                    {uploading
                      ? 'Caricamento…'
                      : mediaItems.length === 0
                        ? 'Seleziona una o più foto (max 10)'
                        : `Aggiungi altre foto (${mediaItems.length}/10)`}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileSelect(e.target.files)}
                  />
                  {mediaItems.length > 0 && (
                    <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 8 }}>
                      {mediaItems.map((m, i) => (
                        <div key={i} style={{ position: 'relative', aspectRatio: '1', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--un-ink-200)' }}>
                          <img src={mediaSrc(m.url)} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                          <button
                            type="button"
                            onClick={() => removeMediaItem(i)}
                            style={{ position: 'absolute', top: 4, right: 4, width: 24, height: 24, padding: 0, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', display: 'grid', placeItems: 'center', cursor: 'pointer' }}
                            title="Rimuovi"
                          >
                            <X width={14} height={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {kind === 'video' && (
                <div style={{ marginBottom: 12 }}>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    style={{
                      width: '100%', padding: '12px 14px',
                      borderRadius: 12, border: '2px dashed var(--un-ink-300)',
                      background: 'var(--un-ink-50)', color: 'var(--un-ink-500)',
                      fontSize: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      cursor: 'pointer'
                    }}
                  >
                    <Upload width={16} height={16} />
                    {uploading
                      ? (uploadProgress > 0 ? `Caricamento ${uploadProgress}%` : 'Caricamento…')
                      : (mediaUrl || streamUid) ? 'Sostituisci video' : 'Seleziona video'}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="video/*"
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileSelect(e.target.files)}
                  />
                  {mediaUrl && (
                    <div style={{ marginTop: 12 }}>
                      <video src={mediaSrc(mediaUrl)} controls style={{ maxHeight: 240, width: '100%', borderRadius: 12, border: '1px solid var(--un-ink-200)' }} />
                    </div>
                  )}
                  {streamUid && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 10, background: 'var(--un-ink-50)', border: '1px solid var(--un-ink-200)', color: 'var(--un-ink-600)', fontSize: 13 }}>
                        <span style={{ color: '#16a34a', fontWeight: 700 }}>✓</span>
                        <span>Video caricato. In elaborazione su Cloudflare — premi <strong>“Pubblica ora”</strong> per inserirlo nella diretta.</span>
                      </div>
                      {buildStreamIframeUrl(streamUid) && (
                        <div style={{ position: 'relative', paddingBottom: '56.25%', marginTop: 8, borderRadius: 12, overflow: 'hidden', border: '1px solid var(--un-ink-200)' }}>
                          <iframe title="Anteprima video" src={buildStreamIframeUrl(streamUid)} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }} allow="autoplay; encrypted-media; picture-in-picture;" allowFullScreen />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {(kind === 'link' || kind === 'article') && (
                <textarea
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder={PLACEHOLDERS[kind]}
                  rows={2}
                  style={{ marginBottom: 12 }}
                />
              )}

              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Didascalia (opzionale)"
                rows={2}
              />

              <div className="composer-foot">
                <div className="tools" />
                <button
                  type="button"
                  className="btn-publish"
                  onClick={handlePublish}
                  disabled={publishing || uploading}
                >
                  <Send width={16} height={16} />
                  {publishing ? 'Pubblicazione…' : 'Pubblica ora'}
                </button>
              </div>
            </section>
          </div>

          {/* Lista contenuti recenti */}
          <section className="card" style={{ marginTop: '4px' }}>
            <div className="card-head">
              <Megaphone size={16} />
              <h2>Contenuti recenti</h2>
            </div>
            {loadingPosts ? (
              <div className="card-body" style={{ textAlign: 'center', padding: '40px' }}>
                <div className="muted" style={{ fontSize: '13px' }}>Caricamento…</div>
              </div>
            ) : posts.length === 0 ? (
              <div className="card-body" style={{ textAlign: 'center', padding: '40px' }}>
                <div className="muted" style={{ fontSize: '13px' }}>Nessun contenuto pubblicato</div>
              </div>
            ) : (
              posts.map((post, idx) => (
                <div
                  key={post._id}
                  style={{ display: 'flex', gap: '14px', padding: '16px 24px', borderBottom: idx === posts.length - 1 ? 'none' : '1px solid var(--un-ink-100)' }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span className="badge s-bozza" style={{ textTransform: 'uppercase' }}>{post.kind}</span>
                      <span className="muted" style={{ fontSize: '11.5px' }}>{formatTime(post.createdAt)}</span>
                      {post.author?.name && (
                        <span className="muted" style={{ fontSize: '11.5px' }}>· {post.author.name}</span>
                      )}
                    </div>

                      {post.kind === 'text' && post.text && (
                        <div
                          className="mt-2 text-sm text-gray-700 dark:text-gray-300 line-clamp-4 prose-sm dark:prose-invert break-words"
                          dangerouslySetInnerHTML={{ __html: post.text }}
                        />
                      )}
                      {post.kind === 'photo' && (post.mediaItems?.length || post.mediaUrl) && (
                        <div className="mt-2 flex gap-1">
                          {(post.mediaItems && post.mediaItems.length > 0
                            ? post.mediaItems
                            : [{ url: post.mediaUrl || '' }]
                          ).slice(0, 3).map((m, i) => (
                            <img
                              key={i}
                              src={mediaSrc(m.url)}
                              alt={post.caption || 'Foto'}
                              className="h-16 w-16 object-cover rounded-md"
                            />
                          ))}
                          {(post.mediaItems?.length || 0) > 3 && (
                            <div className="h-16 w-16 rounded-md bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-xs text-gray-500">
                              +{(post.mediaItems!.length - 3)}
                            </div>
                          )}
                        </div>
                      )}
                      {post.kind === 'video' && post.streamUid && (
                        post.streamStatus === 'ready' && buildStreamIframeUrl(post.streamUid) ? (
                          <div className="mt-2" style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: 8 }}>
                            <iframe title="Video" src={buildStreamIframeUrl(post.streamUid)} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }} allow="autoplay; encrypted-media; picture-in-picture;" allowFullScreen />
                          </div>
                        ) : post.streamStatus === 'error' ? (
                          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Video non disponibile</p>
                        ) : (
                          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">🔴 Video in arrivo — in elaborazione…</p>
                        )
                      )}
                      {post.kind === 'video' && post.mediaUrl && (
                        <video src={mediaSrc(post.mediaUrl)} controls className="mt-2 max-h-40 w-full rounded-lg" />
                      )}
                      {post.kind === 'youtube' && post.youtubeId && (
                        <a
                          href={`https://youtu.be/${post.youtubeId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 inline-flex items-center gap-1 text-xs text-rose-600 dark:text-rose-400 hover:underline"
                        >
                          <VideoIcon className="w-3.5 h-3.5" /> YouTube: {post.youtubeId}
                        </a>
                      )}
                      {(post.kind === 'link' || post.kind === 'article') && (post.linkUrl || post.linkMeta?.title) && (
                        <a
                          href={post.linkUrl || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 hover:underline truncate max-w-full"
                        >
                          <LinkIcon className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="truncate">{post.linkMeta?.title || post.linkUrl}</span>
                        </a>
                      )}
                      {post.caption && (
                        <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400 break-words">{post.caption}</p>
                      )}
                  </div>

                  {post.isOwn && (
                    <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                      <button
                        type="button"
                        className="top-link"
                        style={{ padding: '6px' }}
                        onClick={() => openEdit(post)}
                        title="Modifica"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        type="button"
                        className="top-link exit"
                        style={{ padding: '6px' }}
                        onClick={() => setDeleteTarget(post)}
                        title="Elimina"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </section>
        </>
      )}
      </div>

      {/* Modale modifica */}
      {editTarget && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" onMouseDown={() => setEditTarget(null)}>
          <div
            className="w-full max-w-lg max-h-[90vh] flex flex-col bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex-shrink-0 flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">Modifica contenuto</h3>
              <button
                type="button"
                onClick={() => setEditTarget(null)}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
                aria-label="Chiudi"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              {editTarget.kind === 'text' && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Testo</label>
                  <RichEditor value={editText} onChange={setEditText} height={220} compact />
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Didascalia</label>
                <input
                  type="text"
                  value={editCaption}
                  onChange={(e) => setEditCaption(e.target.value)}
                  placeholder="Didascalia (opzionale)"
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:ring-1 focus:ring-rose-500 focus:border-rose-500 outline-none"
                />
              </div>
            </div>
            <div className="flex-shrink-0 flex justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setEditTarget(null)}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={savingEdit}
                className="px-3 py-1.5 text-sm font-medium text-white bg-rose-600 hover:bg-rose-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {savingEdit ? 'Salvataggio…' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Conferma eliminazione */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Elimina contenuto"
        message="Sei sicuro di voler eliminare questo contenuto dalla diretta? L'azione non e reversibile."
        confirmText={deleting ? 'Eliminazione...' : 'Elimina'}
        type="danger"
      />
    </AssociativeAppShell>
  );
};

export default LiveReporterPage;
